import type {} from '@mui/x-date-pickers-pro/themeAugmentation'
import type {} from '@mui/x-data-grid-premium/themeAugmentation'
import { type ComponentsOverrides, type ComponentsProps, createTheme } from '@mui/material/styles'
import {
  GRID_CHECKBOX_SELECTION_COL_DEF,
  gridClasses,
  GRID_AGGREGATION_ROOT_FOOTER_ROW_ID,
  GRID_REORDER_COL_DEF,
} from '@mui/x-data-grid-premium'
import { omit } from 'lodash-es'
import { type InputAdornmentProps, type SxProps, iconButtonClasses, inputBaseClasses, NoSsr } from '@mui/material'
import { stepConnectorClasses } from '@mui/material/StepConnector'
import { stepLabelClasses } from '@mui/material/StepLabel'
import { stepIconClasses } from '@mui/material/StepIcon'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import * as React from 'react'
import {
  TableRowColorHoveredBackground,
  TableRowColorSelectedBackground,
} from 'settle-ui/components/DataGrid/DataGridRow.tokens'
import {
  handleMouseEnter,
  handleMouseLeave,
} from 'settle-ui/components/DataGrid/helpers/defaultCellSlotProps/defaultCellSlotProps'
import { PickersTextField } from 'settle-ui/components/PickersTextField/PickersTextField'
import * as tokens from './tokens'
import * as switchTokens from './tokens/common/Switch'
import * as badgeTokens from './tokens/common/Badge'
import * as selectorTokens from './tokens/common/Selector'
import * as tooltipTokens from './tokens/common/Tooltip'
import * as inputTokens from './tokens/common/Input'
import * as inputOutlinedTokens from './tokens/common/InputOutlined'
import * as chipTokens from './tokens/common/Chip'
import * as segmentedControlTokens from './tokens/common/SegmentedControl'
import { AutocompleteLoadingText } from './components/Autocomplete/components/AutocompleteLoadingText/AutocompleteLoadingText'
import { HEADER_HEIGHT, ROW_HEIGHT, suiGridClasses } from './components/DataGrid/DataGrid.consts'
import { ACTIONS_COLUMN_DEF } from './components/DataGrid/hooks/useActionsColumnDef/useActionsColumnDef.consts'
import { LEADING_ICON_COLUMN_DEF } from './components/DataGrid/hooks/useLeadingIconColumnDef/useLeadingIconColumnDef.consts'
import { GapColumnDefs } from './components/DataGrid/hooks/useGapColumnDefs/useGapColumnDefs.consts'
import { ColumnMenu } from './components/DataGrid/components/ColumnMenu/ColumnMenu'

/** The single source of stacking order; MUI's own `theme.zIndex` is derived from it below. */
const Z_INDEX = {
  appBar: 1100,
  floatingActionPanel: 1130,
  navigationDrawer: 1200,
  banner: 1200,
  dialog: 1300,
  /** A companion drawer docks over the details dialog it belongs to, so it outranks `dialog`. */
  companionDrawer: 1301,
  /** MUI's popup layer — Popover, Menu, Select, Autocomplete — applied via `zIndex.modal` below.
   * Must outrank every surface a popup opens from, `companionDrawer` included, or a dropdown
   * opened inside one paints behind it. */
  popup: 1310,
  tooltip: 1500,
  thirdPartyOverlay: 10000,
  /** Must clear `thirdPartyOverlay`: an error toast behind the bank-linking iframe is unreadable. */
  snackbar: 11000,
  /** int32 max: the Navattic tour must clear third-party overlays whose z-index we never see. */
  onboardingTour: 2147483647,
} as const

/** Shared sx for dropdown caret IconButtons — matches MuiAutocomplete popupIndicator and MuiSelect icon styling. */
export const caretSx = {
  p: '2px',
  width: 'auto',
  height: 'auto',
  '&:hover': { backgroundColor: 'transparent' },
} as const satisfies SxProps

declare module '@mui/material/styles' {
  interface Theme {
    /**
     * Contains selector you can use to specify custom styles when "obtrusive scrollbars"
     * are turned on the page. See the setScrollbarStrategy helper for more details:
     * https://github.com/team-settle/settle-web/blob/main/src/settle-ui/lib/scrollbarObtrusive/setScrollbarStrategy.ts
     */
    withObtrusiveScrollbar: string
    tokens: tokens.Tokens
    zIndexes: typeof Z_INDEX
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  interface ThemeOptions extends Pick<Theme, 'vars' | 'withObtrusiveScrollbar' | 'tokens' | 'zIndexes'> {}

  interface Palette {
    onSurfacePrimary: string
    onSurfaceSecondary: string
    onSurfaceTertiary: string
    linkPrimary: string
    brand: {
      qboPrimary: string
      xeroPrimary: string
      netSuiteDark: string
      finaloopPrimary: string
    }
  }

  interface TypeText {
    warning: string
  }

  interface PaletteOptions {
    onSurfacePrimary: string
    onSurfaceSecondary: string
    onSurfaceTertiary: string
    linkPrimary: string
    brand: {
      qboPrimary: string
      xeroPrimary: string
      netSuiteDark: string
      finaloopPrimary: string
    }
  }

  interface Components {
    MuiDataGrid?: {
      defaultProps?: ComponentsProps['MuiDataGrid']
      styleOverrides?: ComponentsOverrides['MuiDataGrid']
    }
  }
}

declare module '@mui/material/styles' {
  interface TypographyVariants {
    numericTabularNums: React.CSSProperties
    inherit: React.CSSProperties
  }

  interface TypographyVariantsOptions {
    numericTabularNums?: React.CSSProperties
    inherit: React.CSSProperties
  }

  interface BreakpointOverrides {
    xxl: true
    mobile: true
    tablet: true
    desktop: true
  }
}

declare module '@mui/material/Typography' {
  export interface TypographyPropsVariantOverrides {
    numericTabularNums: true
    inherit: true
  }
}

declare module '@mui/material/IconButton' {
  export interface IconButtonPropsSizeOverrides {
    small: true
    medium: true
    large: true
    tiny: true
  }
}

const body2 = {
  fontSize: tokens.ThemeTypographyDesktopBody2FontSize,
  fontWeight: tokens.ThemeTypographyDesktopBody2FontWeight,
  letterSpacing: tokens.ThemeTypographyDesktopBody2LetterSpacing,
  lineHeight: tokens.ThemeTypographyDesktopBody2LineHeight,
}

export const classNames = {
  // A selector you can use to specify custom styles when "obtrusive scrollbars" are turned on the page.
  obtrusiveScrollbar: 'obtrusiveScrollbar',
} as const

const numericTabularNums = {
  ...body2,
  fontVariantNumeric: 'tabular-nums',
  letterSpacing: 0,
}

// The element holding the value, across every outlined input: `input`, `textarea`, and the select's
// `div[role=combobox]` all carry this class. Adornment buttons don't.
const FOCUSED_INPUT = `.${inputBaseClasses.input}:focus`

export const theme = createTheme({
  // perf. fix: setting vars to null at runtime prevents ThemeProvider object spread on every render
  vars: null as unknown as undefined,
  tokens,
  zIndexes: Z_INDEX,
  // `fab` stays on MUI's default: deriving it would move its two readers 1050 -> 1130 for nothing.
  zIndex: {
    appBar: Z_INDEX.appBar,
    drawer: Z_INDEX.navigationDrawer,
    modal: Z_INDEX.popup,
    snackbar: Z_INDEX.snackbar,
    tooltip: Z_INDEX.tooltip,
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
      xxl: 1920,
      mobile: 900,
      tablet: 1024,
      desktop: 1200,
    },
  },
  typography: {
    numericTabularNums,
    h1: {
      fontSize: tokens.ThemeTypographyDesktopH1FontSize,
      lineHeight: tokens.ThemeTypographyDesktopH1LineHeight,
      // TODO: https://linear.app/settle/issue/FRONT-5001/update-letterspacing-for-headings-in-design-tokens
      // letterSpacing: tokens.ThemeTypographyDesktopH1LetterSpacing,
      // fontWeight: tokens.ThemeTypographyDesktopH1FontWeight,
      letterSpacing: '-0.025em',
      fontWeight: 500,
    },
    h2: {
      fontSize: tokens.ThemeTypographyDesktopH2FontSize,
      lineHeight: tokens.ThemeTypographyDesktopH2LineHeight,
      // letterSpacing: tokens.ThemeTypographyDesktopH2LetterSpacing,
      // fontWeight: tokens.ThemeTypographyDesktopH2FontWeight,
      letterSpacing: '-0.025em',
      fontWeight: 500,
    },
    h3: {
      fontSize: tokens.ThemeTypographyDesktopH3FontSize,
      lineHeight: tokens.ThemeTypographyDesktopH3LineHeight,
      // letterSpacing: tokens.ThemeTypographyDesktopH3LetterSpacing,
      // fontWeight: tokens.ThemeTypographyDesktopH3FontWeight,
      letterSpacing: '-0.025em',
      fontWeight: 500,
    },
    h4: {
      fontSize: tokens.ThemeTypographyDesktopH4FontSize,
      lineHeight: tokens.ThemeTypographyDesktopH4LineHeight,
      // letterSpacing: tokens.ThemeTypographyDesktopH4LetterSpacing,
      // fontWeight: tokens.ThemeTypographyDesktopH4FontWeight,
      letterSpacing: '-0.025em',
      fontWeight: 500,
    },
    h5: {
      fontSize: tokens.ThemeTypographyDesktopH5FontSize,
      fontWeight: tokens.ThemeTypographyDesktopH5FontWeight,
      letterSpacing: tokens.ThemeTypographyDesktopH5LetterSpacing,
      lineHeight: tokens.ThemeTypographyDesktopH5LineHeight,
    },
    h6: {
      fontSize: tokens.ThemeTypographyDesktopH6FontSize,
      fontWeight: tokens.ThemeTypographyDesktopH6FontWeight,
      letterSpacing: tokens.ThemeTypographyDesktopH6LetterSpacing,
      lineHeight: tokens.ThemeTypographyDesktopH6LineHeight,
    },
    caption: {
      fontSize: tokens.ThemeTypographyDesktopCaptionFontSize,
      fontWeight: tokens.ThemeTypographyDesktopCaptionFontWeight,
      letterSpacing: tokens.ThemeTypographyDesktopCaptionLetterSpacing,
      lineHeight: tokens.ThemeTypographyDesktopCaptionLineHeight,
    },
    overline: {
      fontSize: tokens.ThemeTypographyDesktopOverlineFontSize,
      fontWeight: tokens.ThemeTypographyDesktopOverlineFontWeight,
      letterSpacing: tokens.ThemeTypographyDesktopOverlineLetterSpacing,
      lineHeight: tokens.ThemeTypographyDesktopOverlineLineHeight,
    },
    subtitle1: {
      fontSize: tokens.ThemeTypographyDesktopSubtitle1FontSize,
      fontWeight: tokens.ThemeTypographyDesktopSubtitle1FontWeight,
      letterSpacing: tokens.ThemeTypographyDesktopSubtitle1LetterSpacing,
      lineHeight: tokens.ThemeTypographyDesktopSubtitle1LineHeight,
    },
    subtitle2: {
      fontSize: tokens.ThemeTypographyDesktopSubtitle2FontSize,
      fontWeight: tokens.ThemeTypographyDesktopSubtitle2FontWeight,
      letterSpacing: tokens.ThemeTypographyDesktopSubtitle2LetterSpacing,
      lineHeight: tokens.ThemeTypographyDesktopSubtitle2LineHeight,
    },
    body1: {
      fontSize: tokens.ThemeTypographyDesktopBody1FontSize,
      fontWeight: tokens.ThemeTypographyDesktopBody1FontWeight,
      letterSpacing: tokens.ThemeTypographyDesktopBody1LetterSpacing,
      lineHeight: tokens.ThemeTypographyDesktopBody1LineHeight,
    },
    inherit: {
      fontFamily: 'inherit',
      fontSize: 'inherit',
      fontWeight: 'inherit',
      lineHeight: 'inherit',
    },
    body2,
    fontFamily: [
      'Inter',
      'SF Pro Display',
      '-apple-system',
      'BlinkMacSystemFont',
      'Segoe UI',
      'Roboto',
      'Oxygen',
      'Ubuntu',
      'Cantarell',
      'Open Sans',
      'Helvetica Neue',
      'sans-serif',
    ].join(','),
  },
  palette: {
    mode: 'light',
    divider: tokens.ThemeColorCommonSeparator,
    success: {
      main: tokens.ThemeColorCommonSuccess,
    },
    error: {
      main: tokens.ThemeColorCommonError,
    },
    primary: {
      main: tokens.ThemeColorCommonPrimary,
    },
    secondary: {
      main: tokens.ThemeColorCommonSecondary,
    },
    background: {
      paper: '#fff',
      default: '#fff',
    },
    text: {
      primary: tokens.ThemeColorCommonOnSurfacePrimary,
      secondary: tokens.ThemeColorCommonOnSurfaceSecondary,
      warning: tokens.ThemeColorCommonWarning,
    },
    linkPrimary: tokens.ThemeColorCommonOnPrimaryLink,
    onSurfacePrimary: tokens.ThemeColorCommonOnSurfacePrimary,
    onSurfaceSecondary: tokens.ThemeColorCommonOnSurfaceSecondary,
    onSurfaceTertiary: tokens.ThemeColorCommonOnSurfaceTertiary,
    brand: {
      netSuiteDark: '#414141',
      xeroPrimary: '#1DB3D3',
      qboPrimary: '#00892E',
      finaloopPrimary: '#D15820',
    },
  },
  components: {
    MuiListSubheader: {
      styleOverrides: {
        root: {
          lineHeight: '40px',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          minWidth: 'auto',
          textTransform: 'initial',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        containedPrimary: {
          color: tokens.ThemeColorCommonOnPrimary,
        },
        outlined: {
          color: tokens.ThemeColorCommonPrimary,
        },
        containedSecondary: {
          backgroundColor: tokens.ThemeColorCommonDestructive,
          borderColor: tokens.ThemeColorCommonDestructive,
          color: '#fff',
          '&:hover': {
            backgroundColor: tokens.ThemeColorCommonDestructive,
            borderColor: tokens.ThemeColorCommonDestructive,
            color: '#fff',
          },
        },
        outlinedSecondary: {
          borderColor: tokens.ThemeColorCommonDestructive,
          color: tokens.ThemeColorCommonDestructive,
          '&:hover': {
            backgroundColor: tokens.ThemeColorCommonDestructive,
            borderColor: tokens.ThemeColorCommonDestructive,
            color: '#fff',
          },
        },
        outlinedPrimary: {
          borderColor: tokens.ThemeColorCommonPrimary,
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        IconComponent: ArrowDropDownIcon,
      },
      styleOverrides: {
        icon: { right: '12px' },
        select: {
          backgroundColor: 'none',
        },
      },
    },
    MuiLink: {
      defaultProps: {
        underline: 'hover',
        variant: 'body2',
      },
      styleOverrides: {
        root: {
          cursor: 'pointer',
        },
      },
    },
    MuiStepper: {
      defaultProps: {
        alternativeLabel: true,
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          backgroundColor: '#fff',
          borderRadius: inputTokens.InputBaseBorderRadiusMedium,
          '&.Mui-error input::placeholder': {
            color: tokens.ThemeColorCommonError,
          },
        },
        sizeSmall: {
          borderRadius: inputTokens.InputBaseBorderRadiusSmall,
        },
        input: {
          '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
            WebkitAppearance: 'none',
            margin: 0,
          },
          '&::[type=number]': {
            MozAppearance: 'textfield',
          },
        },
      },
    },
    MuiInputAdornment: {
      defaultProps: {
        /**
         * This code wraps the `InputAdornment` component in `<NoSsr>` as a temporary workaround for a
         * known issue in Next.js 14+ causing `className` mismatches between server and client rendering.
         *
         * @see https://github.com/vercel/next.js/issues/57615
         */
        component: ({ component: Component = 'div', ...props }: InputAdornmentProps) => (
          <NoSsr>
            <Component {...props} />
          </NoSsr>
        ),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor: inputOutlinedTokens.InputOutlinedColorDefaultOutline,
          borderWidth: inputOutlinedTokens.InputOutlinedBorderWidthDefault,
        },
        root: {
          backgroundColor: inputOutlinedTokens.InputOutlinedColorDefaultBackground,
          letterSpacing: inputTokens.InputBaseTypographyTextLetterSpacing,
          lineHeight: inputTokens.InputBaseTypographyTextLineHeight,
          fontSize: inputTokens.InputBaseTypographyTextFontSize,

          '&.Mui-focused': {
            backgroundColor: inputOutlinedTokens.InputOutlinedColorFocusedBackground,
            color: inputOutlinedTokens.InputOutlinedColorFocusedText,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: inputOutlinedTokens.InputOutlinedColorFocusedOutline,
            borderWidth: inputOutlinedTokens.InputOutlinedBorderWidthFocused,
          },
          '&.MuiInputBase-readOnly': {
            backgroundColor: inputOutlinedTokens.InputOutlinedColorReadOnlyBackground,
            color: inputOutlinedTokens.InputOutlinedColorReadOnlyText,
            cursor: 'default',

            // Read-only holds MUI's `focused` flag down so the label never floats, so `.Mui-focused`
            // never lands here and the focus styling keys off the DOM instead. `:focus-within` would
            // also fire for a trailing button (copy, autocomplete caret), which stays enabled and
            // carries its own focus ring — this matches the value element only.
            [`&:has(${FOCUSED_INPUT})`]: {
              backgroundColor: inputOutlinedTokens.InputOutlinedColorReadOnlyFocusedBackground,
            },

            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: inputOutlinedTokens.InputOutlinedColorReadOnlyOutline,
              borderWidth: inputOutlinedTokens.InputOutlinedBorderWidthReadOnly,
            },
            // Read-only fields stay tabbable, so they still need a focus affordance — a neutral one,
            // since the brand-colored ring reads as "you can type here".
            [`&:has(${FOCUSED_INPUT}) .MuiOutlinedInput-notchedOutline`]: {
              borderColor: inputOutlinedTokens.InputOutlinedColorReadOnlyFocusedOutline,
              borderWidth: inputOutlinedTokens.InputOutlinedBorderWidthReadOnlyFocused,
            },
            '& .MuiInputAdornment-positionStart': {
              color: inputOutlinedTokens.InputOutlinedColorReadOnlyLeadingIcon,
            },
            '& .MuiInputAdornment-positionEnd': {
              color: inputOutlinedTokens.InputOutlinedColorReadOnlyTrailingIcon,
            },
            '& .MuiSelect-icon': {
              color: inputOutlinedTokens.InputOutlinedColorReadOnlyTrailingIcon,
            },
            // The autocomplete caret sits outside `MuiInputAdornment`, so the rule above never reaches it.
            '& .MuiAutocomplete-popupIndicator': {
              color: inputOutlinedTokens.InputOutlinedColorReadOnlyTrailingIcon,
            },
            // A disabled `IconButton` paints its own color over anything the adornment passes down.
            '& .MuiInputAdornment-positionEnd .MuiIconButton-root': {
              color: inputOutlinedTokens.InputOutlinedColorReadOnlyTrailingIcon,
            },
            // MUI only unsets the select's pointer cursor and text selection for `disabled`.
            '& .MuiSelect-select': {
              cursor: 'default',
              userSelect: 'text',
            },
          },
          '&.Mui-disabled': {
            backgroundColor: inputOutlinedTokens.InputOutlinedColorDisabledBackground,
            color: inputOutlinedTokens.InputOutlinedColorDisabledText,
          },
          '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
            borderColor: inputOutlinedTokens.InputOutlinedColorDisabledOutline,
            borderWidth: inputOutlinedTokens.InputOutlinedBorderWidthDisabled,
          },
          '&.Mui-error': {
            backgroundColor: inputOutlinedTokens.InputOutlinedColorErrorBackground,
            color: inputOutlinedTokens.InputOutlinedColorErrorText,
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: inputOutlinedTokens.InputOutlinedColorErrorOutline,
            borderWidth: inputOutlinedTokens.InputOutlinedBorderWidthError,
          },
          '&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: inputOutlinedTokens.InputOutlinedColorErrorFocusedOutline,
            borderWidth: inputOutlinedTokens.InputOutlinedBorderWidthErrorFocused,
          },
          '&:hover:not(.Mui-focused):not(.Mui-disabled):not(.MuiInputBase-readOnly) .MuiOutlinedInput-notchedOutline': {
            borderColor: inputOutlinedTokens.InputOutlinedColorHoveredOutline,
            borderWidth: inputOutlinedTokens.InputOutlinedBorderWidthHovered,
          },
        },
        input: {
          // This is a "temporary" solution. We will move these
          // hardcoded values to the design tokens in the future.
          '&.Mui-disabled': {
            WebkitTextFillColor: 'rgba(0,0,0,.38)',
          },
          // The root's cursor doesn't reach the inner input, which keeps the UA text caret.
          '&.MuiInputBase-readOnly': {
            cursor: 'default',
          },
        },
        inputSizeSmall: {
          paddingTop: '10px',
          paddingBottom: '10px',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          letterSpacing: inputTokens.InputBaseTypographyTextLetterSpacing,
          lineHeight: inputTokens.InputBaseTypographyTextLineHeight,
          fontSize: inputTokens.InputBaseTypographyTextFontSize,
          color: inputOutlinedTokens.InputOutlinedColorIdleLabel,

          '&.Mui-error': {
            color: inputOutlinedTokens.InputOutlinedColorErrorLabel,
          },
          '&.Mui-focused:not(.Mui-error)': {
            color: inputOutlinedTokens.InputOutlinedColorFocusedLabel,
          },
          '&.Mui-disabled:not(.Mui-error)': {
            color: inputOutlinedTokens.InputOutlinedColorDisabledLabel,
          },
          // The label is a sibling of the input, so read-only is only reachable through the
          // form control they share.
          '.MuiFormControl-root:has(.MuiInputBase-readOnly) &:not(.Mui-error):not(.Mui-disabled)': {
            color: inputOutlinedTokens.InputOutlinedColorReadOnlyLabel,
          },
        },
        outlined: {
          color: inputOutlinedTokens.InputOutlinedColorDefaultLabel,
        },
        asterisk: {
          marginLeft: '-0.25em',
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          letterSpacing: inputTokens.InputBaseTypographyHelperLetterSpacing,
          lineHeight: inputTokens.InputBaseTypographyHelperLineHeight,
          fontSize: inputTokens.InputBaseTypographyHelperFontSize,
          '.MuiFormControl-root:has(.MuiInputBase-readOnly) &:not(.Mui-error):not(.Mui-disabled)': {
            color: inputOutlinedTokens.InputOutlinedColorReadOnlyHelper,
          },
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontSize: '14px',
          lineHeight: '20px',
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          fontSize: '14px',
          lineHeight: '20px',
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        marginNormal: ({ theme }) => ({
          margin: theme.spacing(1, 0),
        }),
        marginDense: ({ theme }) => ({
          margin: theme.spacing(0.5, 0),
        }),
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: '42px',
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          textTransform: 'uppercase',
          fontWeight: 'bold',
          fontSize: '16px',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottomColor: tokens.ThemeColorCommonSeparator,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '14px',
          '&.Mui-disabled': {
            color: tokens.ThemeColorCommonOnSurfaceTertiary,
            opacity: 1,
          },
        },
        gutters: ({ theme }) => ({
          padding: theme.spacing(1, 1.5),
        }),
      },
    },
    MuiMenu: {
      styleOverrides: {
        list: {
          '& .MuiDivider-root': {
            marginTop: 4,
            marginBottom: 4,
          },
        },
      },
    },
    MuiTooltip: {
      defaultProps: {
        disableInteractive: true,
        enterDelay: 500,
        enterTouchDelay: 0,
        placement: 'bottom',
        arrow: false,
      },
      styleOverrides: {
        tooltip: {
          display: 'flex',
          flexDirection: 'column',
          maxWidth: tooltipTokens.TooltipSizeMaxWidthDesktop,
          minHeight: tooltipTokens.TooltipSizeMinHeightDesktop,
          marginLeft: 0,
          marginRight: 0,
          paddingTop: tooltipTokens.TooltipSpacingDesktopVertical,
          paddingBottom: tooltipTokens.TooltipSpacingDesktopVertical,
          paddingLeft: tooltipTokens.TooltipSpacingDesktopHorizontal,
          paddingRight: tooltipTokens.TooltipSpacingDesktopHorizontal,
          lineHeight: tooltipTokens.TooltipTypographyDesktopLineHeight,
          fontWeight: tooltipTokens.TooltipTypographyDesktopFontWeight,
          fontSize: tooltipTokens.TooltipTypographyDesktopFontSize,
          color: tooltipTokens.TooltipColorDefaultText,
          backgroundColor: tooltipTokens.TooltipColorDefaultBackground,
          borderRadius: tooltipTokens.TooltipBorderRadiusDesktop,
          borderWidth: tooltipTokens.TooltipBorderWidthDefault,
          boxShadow: `${tooltipTokens.TooltipShadowDesktopX} ${tooltipTokens.TooltipShadowDesktopY} ${tooltipTokens.TooltipShadowDesktopBlur} ${tooltipTokens.TooltipShadowDesktopSpread} ${tooltipTokens.TooltipShadowDesktopColor}`,
          '&&&': {
            marginTop: tooltipTokens.TooltipSpacingDesktopMarginTop,
          },
          '@media (max-width: 900px)': {
            maxWidth: tooltipTokens.TooltipSizeMaxWidthMobile,
            minHeight: tooltipTokens.TooltipSizeMinHeightMobile,
            paddingTop: tooltipTokens.TooltipSpacingMobileVertical,
            paddingBottom: tooltipTokens.TooltipSpacingMobileVertical,
            paddingLeft: tooltipTokens.TooltipSpacingMobileHorizontal,
            paddingRight: tooltipTokens.TooltipSpacingMobileHorizontal,
            borderRadius: tooltipTokens.TooltipBorderRadiusMobile,
            '&&&': {
              marginTop: tooltipTokens.TooltipSpacingMobileMarginTop,
            },
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        noOptions: {
          fontSize: tokens.ThemeTypographyDesktopBody2FontSize,
          fontWeight: tokens.ThemeTypographyDesktopBody2FontWeight,
          letterSpacing: tokens.ThemeTypographyDesktopBody2LetterSpacing,
          lineHeight: tokens.ThemeTypographyDesktopBody2LineHeight,
          textAlign: 'center',
        },
        option: {
          '&[aria-disabled="true"][aria-disabled="true"]': {
            color: tokens.ThemeColorCommonOnSurfaceTertiary,
            opacity: 1,
          },
        },
        inputRoot: ({ theme }) => ({
          minHeight: `${theme.spacing(5)}!important`,
          paddingTop: `${theme.spacing(0.5)}!important`,
          paddingBottom: `${theme.spacing(0.5)}!important`,
          [`&.${inputBaseClasses.sizeSmall}`]: {
            minHeight: `${theme.spacing(4)}!important`,
            paddingTop: `${theme.spacing(0.5)}!important`,
            paddingBottom: `${theme.spacing(0.5)}!important`,
            paddingLeft: 0,
          },
        }),
        input: ({ theme }) => ({
          paddingTop: '0!important',
          paddingBottom: '0!important',
          minHeight: `${theme.spacing(3)}!important`,
          [`&.${inputBaseClasses.sizeSmall}`]: {
            minHeight: `${theme.spacing(2)}!important`,
            paddingLeft: 0,
          },
        }),
        popupIndicator: {
          padding: '2px',
          '&:hover': {
            backgroundColor: 'transparent',
          },
        },
        clearIndicator: {
          padding: '2px',
          '&:hover': {
            backgroundColor: 'transparent',
          },
        },
        endAdornment: {
          right: '12px !important',
        },
      },
      defaultProps: {
        popupIcon: <ArrowDropDownIcon />,
        slotProps: {
          chip: {
            size: 'small',
            color: 'default',
            variant: 'filled',
          },
        },
        selectOnFocus: false,
        loadingText: <AutocompleteLoadingText />,
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: selectorTokens.SelectorColorUnselectedDefaultBody,
          '&.Mui-checked': {
            color: selectorTokens.SelectorColorSelectedDefaultBody,
          },
          '&.MuiCheckbox-indeterminate': {
            color: selectorTokens.SelectorColorIndeterminateDefaultBody,
          },
          '&.Mui-disabled': {
            color: selectorTokens.SelectorColorUnselectedDisabledBody,
          },
        },
      },
    },
    MuiChip: {
      defaultProps: {
        sx: {
          color: chipTokens.ChipColorDefaultText,
          background: chipTokens.ChipColorDefaultBackground,
        },
      },
      styleOverrides: {
        sizeSmall: {
          height: chipTokens.ChipSizeDesktopSmall,
        },
        labelSmall: ({ theme }) => ({
          fontSize: theme.spacing(1.75),
          lineHeight: theme.spacing(2.5),
        }),
        outlinedSecondary: {
          background: tokens.ThemeColorCommonPrimaryContainer,
        },
        deleteIcon: {
          color: tokens.ThemeColorCommonOnSurfaceTertiary,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          zIndex: Z_INDEX.appBar,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '8px 24px',
          '&:first-of-type': {
            paddingTop: '20px',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        root: {
          ':has(> div[style*="opacity: 0"])': {
            pointerEvents: 'none',
          },
        },
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        root: {
          fontSize: '0.75rem',
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        colorPrimary: {
          backgroundColor: badgeTokens.BadgeColorPrimaryBackground,
          color: badgeTokens.BadgeColorPrimaryText,
        },
        colorSecondary: {
          background: badgeTokens.BadgeColorSecondaryBackground,
          color: badgeTokens.BadgeColorSecondaryText,
        },
        dot: {
          minWidth: badgeTokens.BadgeSizeNumberOffDesktop,
          '@media (max-width: 900px)': {
            minWidth: badgeTokens.BadgeSizeNumberOffMobile,
          },
        },
        standard: {
          minWidth: badgeTokens.BadgeSizeNumberOnDesktop,
          '@media (max-width: 900px)': {
            minWidth: badgeTokens.BadgeSizeNumberOnMobile,
          },
        },
        colorWarning: {
          background: badgeTokens.BadgeColorTertiaryBackground,
          color: badgeTokens.BadgeColorTertiaryText,
        },
        colorInfo: {
          background: badgeTokens.BadgeColorBlankBackground,
          color: badgeTokens.BadgeColorBlankText,
        },
      },
    },
    MuiDesktopDatePicker: {
      defaultProps: {
        minDate: new Date('2018-01-01'),
        slots: {
          textField: PickersTextField,
        },
      },
    },
    MuiStaticDatePicker: {
      defaultProps: {
        minDate: new Date('2018-01-01'),
      },
    },
    MuiPickerDay: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: tokens.ThemeColorCommonSecondary,
          },
        },
      },
    },
    MuiPickersInputBase: {
      styleOverrides: {
        root: {
          '& .MuiInputAdornment-root': {
            marginLeft: 0,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        outlined: {
          background: tokens.ThemeColorCommonSurface,
          border: `1px solid ${tokens.ThemeColorCommonOutline}`,
          borderRadius: '5px',
        },
      },
      defaultProps: {
        elevation: 7,
      },
    },
    MuiCard: {
      defaultProps: {
        variant: 'outlined',
      },
    },
    MuiSwitch: {
      defaultProps: {
        disableRipple: true,
        focusVisibleClassName: '.Mui-focusVisible',
      },
      styleOverrides: {
        root: ({ theme, ownerState }) => ({
          width: switchTokens.ToggleSizeAppleDesktopBodyWidth,
          height: switchTokens.ToggleSizeAppleDesktopBodyHeight,
          padding: 0,
          margin: '2px 7px',
          ...(ownerState.size === 'medium' && {
            width: switchTokens.ToggleSizeAppleDesktopBodyWidth,
            height: switchTokens.ToggleSizeAppleDesktopBodyHeight,
            margin: '4px 11px',
          }),
          '& .MuiSwitch-switchBase': {
            padding: 0,
            color: switchTokens.ToggleColorUnselectedDefaultKnob,
            top: `calc(50% - ${switchTokens.ToggleSizeAppleDesktopKnob}/2)`,
            left: 2,
            transitionDuration: '300ms',
            ...(ownerState.size === 'medium' && {
              top: `calc(50% - ${switchTokens.ToggleSizeAppleDesktopKnob}/2)`,
            }),
            '&.Mui-checked': {
              color: switchTokens.ToggleColorSelectedDefaultKnob,
              transform: `translateX(calc(${switchTokens.ToggleSizeAppleDesktopBodyWidth} - ${switchTokens.ToggleSizeAppleDesktopKnob} - 4px))`,
              '& + .MuiSwitch-track': {
                opacity: 1,
                backgroundColor: switchTokens.ToggleColorSelectedDefaultBody,
              },
              '&.Mui-disabled + .MuiSwitch-track': {
                backgroundColor: switchTokens.ToggleColorSelectedDisabledBody,
              },
              '&.Mui-disabled': {
                color: switchTokens.ToggleColorUnselectedDisabledKnob,
              },
            },
            '&.Mui-disabled + .MuiSwitch-track': {
              opacity: 1,
              backgroundColor: switchTokens.ToggleColorUnselectedDisabledBody,
            },
            '&.Mui-disabled': {
              opacity: 1,
              color: switchTokens.ToggleColorSelectedDisabledKnob,
            },
          },
          '& .MuiSwitch-thumb': {
            width: switchTokens.ToggleSizeAppleDesktopKnob,
            height: switchTokens.ToggleSizeAppleDesktopKnob,
            borderRadius: switchTokens.ToggleBorderRadiusAppleDesktopKnob,
            boxShadow: 'none',
            ...(ownerState.size === 'medium' && {
              width: switchTokens.ToggleSizeAppleDesktopKnob,
              height: switchTokens.ToggleSizeAppleDesktopKnob,
              borderRadius: switchTokens.ToggleBorderRadiusAppleDesktopKnob,
            }),
          },
          '& .MuiSwitch-track': {
            opacity: 1,
            backgroundColor: switchTokens.ToggleColorUnselectedDefaultBody,
            borderRadius: switchTokens.ToggleBorderRadiusAppleDesktopBody,
            transition: theme.transitions.create(['background-color'], {
              duration: 500,
            }),
            ...(ownerState.size === 'medium' && {
              borderRadius: switchTokens.ToggleBorderRadiusAppleDesktopBody,
            }),
          },
        }),
      },
    },
    // TODO: FRONT-4539 fix bug due to react-transition-group bug
    MuiBackdrop: {
      styleOverrides: {
        root: {
          '&[style*="opacity: 0"]': {
            pointerEvents: 'none',
          },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: ({ theme, ownerState }) => ({
          padding: 0,
          boxShadow: 'none',
          borderRadius: ownerState.square ? 0 : theme.spacing(0.5),
          border: `1px solid ${theme.tokens.ThemeColorCommonSeparator}`,
          '&:before': {
            display: 'none',
          },
          '&.Mui-expanded': {
            margin: 0,
          },
        }),
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: theme.spacing(1, 3),
          '& .MuiAccordionSummary-content.Mui-expanded': {
            margin: theme.spacing(1.5, 0),
          },
          '& .MuiAccordionSummary-expandIconWrapper': {
            order: -1,
            marginRight: theme.spacing(3),
            '&.Mui-expanded': {
              transform: 'rotate(90deg)',
            },
          },
        }),
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: theme.spacing(0, 0, 1),
          borderTop: `1px solid ${theme.tokens.ThemeColorCommonSeparator}`,
        }),
      },
    },
    MuiDataGrid: {
      defaultProps: {
        rowHeight: ROW_HEIGHT.default,
        columnHeaderHeight: HEADER_HEIGHT.default,
        hideFooter: true,
        editMode: 'row',
        disableAggregation: true,
        disableRowGrouping: true,
        disableVirtualization: true,
        disableRowSelectionOnClick: true,
        disableMultipleColumnsSorting: true,
        hideFooterSelectedRowCount: true,
        disableColumnPinning: true,
        disableColumnFilter: true,
        disableColumnReorder: true,
        disableColumnSelector: true,
        disableDensitySelector: true,
        disableMultipleColumnsFiltering: true,
        disableColumnMenu: true,
        disablePivoting: true,
        slots: {
          columnMenu: ColumnMenu,
        },
        slotProps: {
          cell: {
            onMouseLeave: handleMouseLeave,
            onMouseEnter: handleMouseEnter,
          },
          loadingOverlay: {
            variant: 'linear-progress',
            noRowsVariant: 'skeleton',
          },
        },
      },
      styleOverrides: {
        root: {
          '--SuiDataGrid-editCellInset': '4px 4px 4px 4px',
          '--SuiDataGrid-editCellInsetColor': `${tokens.ThemeColorCommonOutline}`,
          // increase specificity to override the default DataGrid paddings
          '&&': { padding: 0 },
          border: 'none',
          '--DataGrid-rowBorderColor': tokens.ThemeColorCommonSeparator,
          [`& .${gridClasses.cell}:not(.separate) + .separate`]: {
            borderLeft: `1px solid var(--DataGrid-rowBorderColor)`,
          },
          [`& .separate + .${gridClasses.cell}:not(.separate)`]: {
            borderLeft: `1px solid var(--DataGrid-rowBorderColor)`,
          },
          [`& .${gridClasses.checkboxInput}`]: {
            height: '32px',
            width: '32px',
          },
          [`& .${iconButtonClasses.root}.${iconButtonClasses.sizeSmall}`]: {
            borderRadius: '4px',
          },
          [`& .${gridClasses.filler} > *`]: {
            border: 'none',
          },
          [`& .${suiGridClasses.underlineDotted}`]: {
            textDecoration: 'underline dotted',
          },
        },
        columnHeaderTitle: {
          fontWeight: 400,
        },
        columnHeader: {
          color: tokens.ThemeColorCommonOnSurfaceSecondary,
          paddingLeft: '12px',
          paddingRight: '12px',
          '&[aria-colindex="1"]': {
            paddingLeft: '16px',
          },
          [`&.${gridClasses['columnHeader--last']}`]: {
            paddingRight: '16px',
          },
          [`&[data-field="${ACTIONS_COLUMN_DEF}"]`]: {
            paddingLeft: '4px',
            paddingRight: '8px',
          },
          [`&[data-field="${LEADING_ICON_COLUMN_DEF}"]`]: {
            paddingLeft: '16px',
            paddingRight: '4px',
          },
          [`&[data-field="${GRID_CHECKBOX_SELECTION_COL_DEF.field}"]`]: {
            paddingLeft: '16px',
            paddingRight: '4px',
            [`& .${gridClasses.columnHeaderTitleContainerContent}`]: {
              position: 'absolute',
            },
          },
          [`&[data-field="${GRID_REORDER_COL_DEF.field}"]`]: {
            paddingLeft: '4px',
            paddingRight: '0px',
            width: '20px !important',
          },
          [`&[data-field="${GapColumnDefs.LEFT_GAP}"], &[data-field="${GapColumnDefs.RIGHT_GAP}"]`]: {
            paddingLeft: 0,
            paddingRight: 0,
            border: 'none',
          },
          '&:focus, &:focus-within': {
            outline: 'none',
          },
          // TODO: to be updated once the design team creates the tiny size
          // https://linear.app/settle/issue/DES-772/add-tiny-button-variation-to-the-ds-tokens
          [`& .${iconButtonClasses.root}.${iconButtonClasses.sizeSmall}`]: {
            height: '24px',
            width: '24px',
          },
          [`&.${suiGridClasses.borderLeft}`]: {
            borderLeft: `1px solid var(--DataGrid-rowBorderColor)`,
          },
          [`&.${suiGridClasses.cellBackgroundSecondaryContainer}`]: {
            background: tokens.ThemeColorCommonSecondaryContainer,
          },
          [`&.${suiGridClasses.borderRight}`]: {
            borderRight: `1px solid var(--DataGrid-rowBorderColor)`,
          },
          [`& .${gridClasses.columnSeparator}`]: {
            [`&:not(.${gridClasses['columnSeparator--resizable']})`]: {
              display: 'none',
            },
          },
          [`&.${gridClasses['columnHeader--filledGroup']}`]: {
            [`& .${gridClasses.columnHeaderTitleContainer}`]: {
              borderBottom: 'none',
            },
            [`& .${gridClasses.columnHeaderTitle}`]: {
              color: tokens.ThemeColorCommonOnSurfacePrimary,
              fontWeight: 500,
            },
            [`&.${gridClasses['columnHeader--pinnedLeft']}`]: {
              borderRight: `1px solid var(--DataGrid-rowBorderColor)`,
            },
          },
        },
        row: {
          [`&.${gridClasses['row--editing']}, &.Mui-selected`]: {
            boxShadow: 'none',
            [[
              `& .${gridClasses.cell}`,
              `& .${gridClasses.cellEmpty}`,
              `& .${gridClasses.cell}.${gridClasses['cell--pinnedRight']}`,
              `& .${gridClasses.cell}.${gridClasses['cell--pinnedLeft']}`,
            ].join(', ')]: {
              background: `
                linear-gradient(${TableRowColorSelectedBackground}, ${TableRowColorSelectedBackground}),
                linear-gradient(var(--DataGrid-t-header-background-base), var(--DataGrid-t-header-background-base))`,
            },
          },
          [`&.${suiGridClasses.activeRow} .${gridClasses.cell}`]: {
            background: `
                linear-gradient(${TableRowColorSelectedBackground}, ${TableRowColorSelectedBackground}),
                linear-gradient(var(--DataGrid-t-header-background-base), var(--DataGrid-t-header-background-base))`,
          },
          '&:hover': {
            [`& .${gridClasses.cell}`]: {
              [`&.${gridClasses['cell--editable']}:not(.${gridClasses['cell--editing']})`]: {
                cursor: 'pointer',
                position: 'relative',
                [`&.${gridClasses['cell--pinnedRight']}, &.${gridClasses['cell--pinnedLeft']}`]: {
                  position: 'sticky',
                },
                [`&::after`]: {
                  content: '""',
                  position: 'absolute',
                  inset: 'var(--SuiDataGrid-editCellInset)',
                  borderRadius: '4px',
                  border: `1px solid var(--SuiDataGrid-editCellInsetColor)`,
                },
              },
            },
            [[
              `& .${gridClasses.cell}`,
              `& .${gridClasses.cellEmpty}`,
              `& .${gridClasses.cell}.${gridClasses['cell--pinnedRight']}`,
              `& .${gridClasses.cell}.${gridClasses['cell--pinnedLeft']}`,
            ].join(', ')]: {
              background: `
                linear-gradient(${TableRowColorHoveredBackground}, ${TableRowColorHoveredBackground}),
                linear-gradient(var(--DataGrid-t-header-background-base), var(--DataGrid-t-header-background-base))`,
            },
          },
          [`&.${suiGridClasses.rowError}`]: {
            [[
              `& .${gridClasses.cell}`,
              `& .${gridClasses.cellEmpty}`,
              `& .${gridClasses.cell}.${gridClasses['cell--pinnedRight']}`,
              `& .${gridClasses.cell}.${gridClasses['cell--pinnedLeft']}`,
              `&.${gridClasses['row--editing']} .${gridClasses.cell}`,
              `&.${gridClasses['row--editing']} .${gridClasses.cellEmpty}`,
              `&.${gridClasses['row--editing']} .${gridClasses.cell}.${gridClasses['cell--pinnedRight']}`,
              `&.${gridClasses['row--editing']} .${gridClasses.cell}.${gridClasses['cell--pinnedLeft']}`,
              `&:hover .${gridClasses.cell}`,
              `&:hover .${gridClasses.cellEmpty}`,
              `&:hover .${gridClasses.cell}.${gridClasses['cell--pinnedRight']}`,
              `&:hover .${gridClasses.cell}.${gridClasses['cell--pinnedLeft']}`,
            ].join(', ')]: {
              background: `
                linear-gradient(${tokens.ThemeColorCommonErrorContainer}, ${tokens.ThemeColorCommonErrorContainer}),
                linear-gradient(var(--DataGrid-t-header-background-base), var(--DataGrid-t-header-background-base))`,
            },
            [[
              `& .${gridClasses.cell}.${suiGridClasses.cellBackgroundSecondaryContainer}`,
              `&:hover .${gridClasses.cell}.${suiGridClasses.cellBackgroundSecondaryContainer}`,
              `&.${gridClasses['row--editing']} .${gridClasses.cell}.${suiGridClasses.cellBackgroundSecondaryContainer}`,
            ].join(', ')]: {
              background: `
                linear-gradient(${TableRowColorSelectedBackground}, ${TableRowColorSelectedBackground}),
                linear-gradient(${tokens.ThemeColorCommonErrorContainer}, ${tokens.ThemeColorCommonErrorContainer}),
                linear-gradient(var(--DataGrid-t-header-background-base), var(--DataGrid-t-header-background-base))`,
            },
          },
          [`&.${gridClasses['row--editable']}:not([data-id="${GRID_AGGREGATION_ROOT_FOOTER_ROW_ID}"])`]: {
            cursor: 'pointer',
          },
          [`&:not([data-id="${GRID_AGGREGATION_ROOT_FOOTER_ROW_ID}"])`]: {
            [`&:hover, &.${gridClasses['row--editing']}`]: {
              [`.${suiGridClasses.cellBackgroundSecondaryContainer}`]: {
                background: `linear-gradient(${TableRowColorSelectedBackground}, ${TableRowColorSelectedBackground}),
                   linear-gradient(${tokens.ThemeColorCommonSecondaryContainer}, ${tokens.ThemeColorCommonSecondaryContainer})`,
              },
            },
            [`.${suiGridClasses.cellBackgroundSecondaryContainer}`]: {
              background: tokens.ThemeColorCommonSecondaryContainer,
            },
          },
        },
        cell: {
          [`&.${suiGridClasses.rightAdornment}, &.${suiGridClasses.rightAdornment}.${gridClasses['cell--editing']}`]: {
            position: 'relative',
            '--SuiDataGrid-editCellInset': '4px 4px 4px 4px',
          },
          [`&.${suiGridClasses.leftAdornment}, &.${suiGridClasses.leftAdornment}.${gridClasses['cell--editing']}`]: {
            position: 'relative',
            '--SuiDataGrid-editCellInset': '4px 4px 4px 48px',
            [`&:hover`]: {
              paddingLeft: '12px',
            },
          },
          [`&.${suiGridClasses.cellError}`]: {
            '--SuiDataGrid-editCellInsetColor': tokens.ThemeColorCommonError,
          },
          color: tokens.ThemeColorCommonOnSurfacePrimary,
          paddingRight: '12px',
          paddingLeft: '12px',
          [`&.${suiGridClasses.customRenderCell}`]: {
            paddingRight: '4px',
            paddingLeft: '4px',
          },
          [`&.${gridClasses['cell--flex']}`]: {
            alignItems: 'center',
          },
          [`&.${gridClasses['cell--editing']}`]: {
            paddingLeft: '4px',
            paddingRight: '4px',
            paddingTop: '4px',
            paddingBottom: '4px',
          },
          [`&.${suiGridClasses.lastVisibleColumn}`]: {
            paddingRight: '16px',
            '--SuiDataGrid-editCellInset': '4px 8px 4px 4px',
            [`&.${gridClasses['cell--editing']}`]: {
              paddingRight: '8px',
            },
          },
          [`&.${suiGridClasses.firstVisibleColumn}`]: {
            paddingLeft: '16px',
            '--SuiDataGrid-editCellInset': '4px 4px 4px 8px',
            [`&.${gridClasses['cell--editing']}`]: {
              paddingLeft: '8px',
            },
          },
          [`&[data-field="${ACTIONS_COLUMN_DEF}"]`]: {
            paddingLeft: '4px',
            paddingRight: '8px',
            '& button[aria-expanded="true"]': {
              color: tokens.ThemeColorCommonSecondary,
              backgroundColor: '#00796b29',
              opacity: '1 !important',
            },
          },
          [`&[data-field="${LEADING_ICON_COLUMN_DEF}"]`]: {
            paddingLeft: '16px',
            paddingRight: '4px',
          },
          [`&[data-field="${GRID_CHECKBOX_SELECTION_COL_DEF.field}"]`]: {
            paddingLeft: '16px',
            paddingRight: '4px',
          },
          [`&[data-field="${GRID_REORDER_COL_DEF.field}"]`]: {
            paddingLeft: '4px',
            paddingRight: '0px',
            width: '20px !important',
            '& svg': {
              fontSize: '16px',
              color: tokens.ThemeColorCommonOnSurfaceTertiary,
            },
          },
          [`&[data-field="${GapColumnDefs.LEFT_GAP}"], &[data-field="${GapColumnDefs.RIGHT_GAP}"]`]: {
            paddingLeft: 0,
            paddingRight: 0,
            border: 'none',
          },
          '&:focus, &:focus-within': {
            outline: 'none!important', // important to fight cell--editing and row--editing modifiers
          },
          [`&.${suiGridClasses.borderLeft}`]: {
            borderLeft: `1px solid var(--DataGrid-rowBorderColor)`,
          },
          [`&.${suiGridClasses.borderRight}`]: {
            borderRight: `1px solid var(--DataGrid-rowBorderColor)`,
          },
          [`&.${suiGridClasses.bold}`]: {
            fontWeight: '600!important',
          },
          [`&.${suiGridClasses.secondary}`]: {
            color: tokens.ThemeColorCommonOnSurfaceSecondary,
          },
          [`&.${suiGridClasses.disabled}`]: {
            color: tokens.ThemeColorCommonOnSurfaceTertiary,
            pointerEvents: 'none',
          },
          // Empty-cell dash → tertiary: `.tertiary` for plain cells, `[data-sui-dash]` for <Dash/> in a renderCell.
          // `.tertiary` skips edit mode so the color doesn't bleed into the cell's input while typing.
          [`&.${suiGridClasses.tertiary}:not(.${gridClasses['cell--editing']})`]: {
            color: tokens.ThemeColorCommonOnSurfaceTertiary,
          },
          '& [data-sui-dash]': {
            color: tokens.ThemeColorCommonOnSurfaceTertiary,
          },
          [`&.${suiGridClasses.numericTabularNums}`]: {
            ...omit(numericTabularNums, 'lineHeight'),
          },
          [`&.${gridClasses.cellEmpty}`]: {
            padding: 0,
          },
          [`&.${suiGridClasses.disableGutters}`]: {
            padding: '4px',
          },
          [`& .${gridClasses.footerCell}`]: {
            fontWeight: 'inherit',
            color: 'inherit',
          },
        },
        footerContainer: {
          minHeight: '40px',
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        displayedRows: {
          color: tokens.ThemeColorCommonOnSurfaceSecondary,
        },
        selectLabel: {
          color: tokens.ThemeColorCommonOnSurfaceSecondary,
        },
        select: {
          color: tokens.ThemeColorCommonOnSurfaceSecondary,
        },
        selectIcon: {
          right: 0,
        },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          backgroundColor: tokens.ThemeColorCommonBackground,
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          border: 'none',
          textTransform: 'none',
          fontSize: '14px',
          fontWeight: 500,
          letterSpacing: '0.25px',
          color: segmentedControlTokens.SegmentedControlItemColorDefaultOnBackground,
          backgroundColor: segmentedControlTokens.SegmentedControlItemColorDefaultBackground,
          '&.Mui-selected': {
            color: segmentedControlTokens.SegmentedControlItemColorPressedOnBackground,
            backgroundColor: segmentedControlTokens.SegmentedControlItemColorPressedBackground,
          },
          '&.Mui-disabled': {
            border: 'none',
          },
          '&:not(.Mui-selected) + &:not(.Mui-selected):before': {
            content: '""',
            position: 'absolute',
            left: 0,
            borderLeft: `1px solid ${tokens.ThemeColorCommonSeparator}`,
          },
          '&:hover:before, &:hover + &:not(.Mui-selected):before': {
            display: 'none',
          },
        },
        sizeSmall: ({ theme }) => ({
          minWidth: '32px',
          padding: theme.spacing(0.5, 1),
          borderRadius: '4px',
          '&:not(.Mui-selected) + &:not(.Mui-selected):before': {
            top: '6px',
            bottom: '6px',
          },
        }),
        sizeMedium: ({ theme }) => ({
          minWidth: '48px',
          padding: theme.spacing(1),
          borderRadius: '5px',
          '&:not(.Mui-selected) + &:not(.Mui-selected):before': {
            top: '8px',
            bottom: '8px',
          },
        }),
      },
    },
    MuiLinearProgress: {
      defaultProps: {
        color: 'secondary',
      },
    },
    MuiStepConnector: {
      styleOverrides: {
        root: {
          [`&.${stepConnectorClasses.alternativeLabel}`]: {
            top: 10,
            left: 'calc(-50% + 16px)',
            right: 'calc(50% + 16px)',
          },
          [`&.${stepConnectorClasses.active}`]: {
            [`& .${stepConnectorClasses.line}`]: {
              borderColor: tokens.ThemeColorCommonOnSurfaceLink,
            },
          },
          [`&.${stepConnectorClasses.completed}`]: {
            [`& .${stepConnectorClasses.line}`]: {
              borderColor: tokens.ThemeColorCommonOnSurfaceLink,
            },
          },
          [`& .${stepConnectorClasses.line}`]: {
            borderColor: tokens.ThemeColorCommonSeparator,
            borderTopWidth: '6px',
            borderRadius: '3px',
          },
        },
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          [`&.${stepIconClasses.active}`]: {
            color: tokens.ThemeColorCommonOnSurfaceLink,
          },
          [`&.${stepIconClasses.completed}`]: {
            color: tokens.ThemeColorCommonOnSurfaceLink,
          },
          color: tokens.ThemeColorCommonSeparator,
        },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        label: ({ theme }) => ({
          [`&.${stepLabelClasses.active}`]: {
            color: tokens.ThemeColorCommonOnSurfacePrimary,
          },
          [`&.${stepLabelClasses.completed}`]: {
            color: tokens.ThemeColorCommonOnSurfaceTertiary,
          },
          [`&.${stepLabelClasses.alternativeLabel}`]: {
            marginTop: theme.spacing(1),
          },
          color: tokens.ThemeColorCommonOnSurfaceTertiary,
          letterSpacing: 0,
          fontWeight: 400,
          fontSize: '12px',
        }),
      },
    },
  },
  withObtrusiveScrollbar: ` .${classNames.obtrusiveScrollbar} &`,
})

export const prideMonthTheme = createTheme(theme, {
  // perf. fix: setting vars to null at runtime prevents ThemeProvider object spread on every render
  vars: null as unknown as undefined,
  components: {
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          backgroundColor: '#F7F6F6',
        },
        bar: {
          background:
            'linear-gradient(90deg, #F27B79 0%, #FFB18F 20%, #FFEF91 40%, #82BF92 60%, #778AD1 80%, #C39CD9 99%)',
        },
      },
    },
  },
})
